import {
	DocumentFormat,
	DocumentFormatValues,
	DocumentTemplate,
	GFA_GENERATE_DOCUMENT_TYPE_CONFIG,
	GFAProjectDocumentGenerationTypeKeys,
} from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { FileCode, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useGetDocumentTemplates } from "../../../../api/queries/documents.queries";
import {
	useGetDuneSignatories,
	useMe,
} from "../../../../api/queries/users.queries";
import { useGenerateMultipleGFADocuments } from "../../../../api/mutations/gfa.mutations";

interface GenerateGFAConstructionSiteDocumentsModalProps {
	types: GFAProjectDocumentGenerationTypeKeys[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	gfaConstructionSiteId: number;
	onSuccess?: () => void;
}

const GenerateGFAConstructionSiteDocumentsModal: React.FC<
	GenerateGFAConstructionSiteDocumentsModalProps
> = ({ types, open, onOpenChange, gfaConstructionSiteId, onSuccess }) => {
	const [documentConfigs, setDocumentConfigs] = useState<
		Record<
			GFAProjectDocumentGenerationTypeKeys,
			{ templateId: string; format: DocumentFormatValues }
		>
	>(() => {
		const initial: Record<
			string,
			{ templateId: string; format: DocumentFormatValues }
		> = {};
		for (const t of types) {
			initial[t] = { templateId: "", format: DocumentFormat.PDF };
		}
		return initial;
	});

	const [selectedSignatoryId, setSelectedSignatoryId] = useState<string>("");

	const { data: allTemplates, isLoading: isLoadingTemplates } =
		useGetDocumentTemplates();

	const { data: duneSignatories, isLoading: isLoadingSignatories } =
		useGetDuneSignatories();

	const { me } = useMe();

	const getValidTemplatesForType = useCallback(
		(docType: GFAProjectDocumentGenerationTypeKeys) => {
			if (!allTemplates) return [];

			const typeConfig = GFA_GENERATE_DOCUMENT_TYPE_CONFIG[docType];
			if (!typeConfig.getTemplateType) return [];

			const gfaSpecificTypes: GFAProjectDocumentGenerationTypeKeys[] = [
				"DEVIS_GFA",
				"RAPPORT_EXPERTISE_GFA",
				"INDICATION_TARIFAIRE_GFA_CARAVELA",
			];
			const expectedTemplateType = gfaSpecificTypes.includes(docType)
				? typeConfig.getTemplateType()
				: typeConfig.getTemplateType("GFA");

			if (!expectedTemplateType) return [];

			return allTemplates.filter(
				(template: DocumentTemplate) =>
					template.validated &&
					template.templateType === expectedTemplateType &&
					template.entityModel === "GFA_CONSTRUCTION_SITE",
			);
		},
		[allTemplates],
	);

	useEffect(() => {
		if (me && duneSignatories && !selectedSignatoryId) {
			const currentUserSignatory = duneSignatories.find(
				(signatory) => signatory.id === me.id,
			);
			if (currentUserSignatory) {
				setSelectedSignatoryId(currentUserSignatory.id.toString());
			}
		}
	}, [me, duneSignatories, selectedSignatoryId]);

	const multiMutation = useGenerateMultipleGFADocuments(gfaConstructionSiteId);

	const handleGenerate = async () => {
		if (!selectedSignatoryId) {
			toast.error("Veuillez sélectionner un signataire Dune");
			return;
		}

		const documentsToGenerate = types
			.filter((t) => documentConfigs[t]?.templateId)
			.map((t) => ({
				type: t,
				templateId: documentConfigs[t].templateId,
				format: documentConfigs[t].format,
			}));

		if (documentsToGenerate.length === 0) {
			toast.error("Veuillez sélectionner au moins un document à générer");
			return;
		}

		multiMutation.mutate(
			{
				documents: documentsToGenerate,
				signataireDuneId: Number(selectedSignatoryId),
			},
			{
				onSuccess: async (data) => {
					toast.success(data.message);
					onOpenChange(false);
					if (onSuccess) {
						onSuccess();
					}
				},
				onError: (error: unknown) => {
					const message =
						error instanceof Error
							? error.message
							: "Erreur lors de la génération";
					toast.error(message);
				},
			},
		);
	};

	const formatOptions = [
		{
			value: DocumentFormat.PDF,
			label: "PDF",
			description: "Pour signature électronique",
			icon: FileText,
		},
		{
			value: DocumentFormat.DOCX,
			label: "Word",
			description: "Pour permettre la modification",
			icon: FileCode,
		},
	];

	const hasAtLeastOneSelection = types.some(
		(t) => documentConfigs[t]?.templateId,
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Générer des documents GFA</DialogTitle>
					<DialogDescription>
						Sélectionnez les modèles, le signataire Dune et les formats de
						documents
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{types.map((docType) => {
						const typeConfig = GFA_GENERATE_DOCUMENT_TYPE_CONFIG[docType];
						const validTemplates = getValidTemplatesForType(docType);

						return (
							<div key={docType} className="space-y-4 border rounded-lg p-4">
								<h3 className="font-medium text-sm">
									{typeConfig.title || docType}
								</h3>

								<div className="space-y-2">
									<label
										htmlFor={`template-select-${docType}`}
										className="text-sm font-medium"
									>
										{typeConfig.templateLabel || "Modèle"}
									</label>
									<Select
										value={documentConfigs[docType]?.templateId || ""}
										onValueChange={(value) =>
											setDocumentConfigs((prev) => ({
												...prev,
												[docType]: {
													...prev[docType],
													templateId: value,
												},
											}))
										}
										disabled={isLoadingTemplates || validTemplates.length === 0}
									>
										<SelectTrigger id={`template-select-${docType}`}>
											<SelectValue
												placeholder={
													isLoadingTemplates
														? "Chargement..."
														: validTemplates.length === 0
															? "Aucun modèle disponible"
															: "Sélectionnez un modèle"
												}
											/>
										</SelectTrigger>
										<SelectContent>
											{validTemplates.map((template: DocumentTemplate) => (
												<SelectItem key={template.id} value={template.id}>
													{template.name} (v{template.version})
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{documentConfigs[docType]?.templateId && (
									<fieldset className="space-y-2">
										<legend className="text-sm font-medium">
											Format du document
										</legend>
										<RadioGroup
											value={
												documentConfigs[docType]?.format || DocumentFormat.PDF
											}
											onValueChange={(value) =>
												setDocumentConfigs((prev) => ({
													...prev,
													[docType]: {
														...prev[docType],
														format: value as DocumentFormatValues,
													},
												}))
											}
											className="grid grid-cols-2 gap-3"
										>
											{formatOptions.map((option) => {
												const Icon = option.icon;
												return (
													<Label
														key={`${docType}-${option.value}`}
														htmlFor={`${docType}-${option.value}`}
														className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
													>
														<RadioGroupItem
															id={`${docType}-${option.value}`}
															value={option.value}
															className="absolute top-2 right-2"
														/>
														<Icon className="h-6 w-6" />
														<span className="font-medium text-xs text-center">
															{option.label}
														</span>
													</Label>
												);
											})}
										</RadioGroup>
									</fieldset>
								)}
							</div>
						);
					})}

					<div className="space-y-2">
						<label htmlFor="signatory-select" className="text-sm font-medium">
							Signataire Dune
						</label>
						<Select
							value={selectedSignatoryId}
							onValueChange={setSelectedSignatoryId}
							disabled={isLoadingSignatories || !duneSignatories?.length}
						>
							<SelectTrigger id="signatory-select">
								<SelectValue
									placeholder={
										isLoadingSignatories
											? "Chargement..."
											: !duneSignatories?.length
												? "Aucun signataire disponible"
												: "Sélectionnez un signataire"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{duneSignatories?.map((signatory) => (
									<SelectItem
										key={signatory.id}
										value={signatory.id.toString()}
									>
										{signatory.firstname} {signatory.lastname}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={
							!selectedSignatoryId ||
							!hasAtLeastOneSelection ||
							multiMutation.isPending
						}
						isLoading={multiMutation.isPending}
					>
						Générer les documents
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default GenerateGFAConstructionSiteDocumentsModal;
